/**
 * Path 4: The Future — Where this is going.
 *
 * 4 scenes showing the vision:
 * autonomous agent memory, continuous learning, team memory, self-healing.
 */

import type { AdventurePath } from './paths';

export const THE_FUTURE_PATH: AdventurePath = {
  id: 'future',
  title: 'The Future',
  subtitle: 'From solo memory to team intelligence',
  scenes: [
    // ── Scene 1: Autonomous Agent Memory ─────────────
    {
      id: 'future-1',
      title: 'Autonomous Agent Memory',
      lines: [
        { text: '$ cortex orchestrate --agents 4 --shared-memory', style: 'accent' },
        { text: '', style: 'blank' },
        { text: '=== Multi-Agent Orchestration ===', style: 'header' },
        { text: '', style: 'blank' },
        { text: '  Agent 1: test-orchestrator', style: 'data' },
        { text: '    Smart test selection. Knows which tests', style: 'dim' },
        { text: '    are relevant to your change.', style: 'dim' },
        { text: '    Memory: 847 test-to-code mappings', style: 'data' },
        { text: '', style: 'blank' },
        { text: '  Agent 2: deploy-guardian', style: 'data' },
        { text: '    Pre-deploy validation. Blocks bad pushes.', style: 'dim' },
        { text: '    Memory: 23 deployment failure patterns', style: 'data' },
        { text: '', style: 'blank' },
        { text: '  Agent 3: data-integrity-watchdog', style: 'data' },
        { text: '    Monitors data quality across pipelines.', style: 'dim' },
        { text: '    Memory: 156 data quality rules', style: 'data' },
        { text: '', style: 'blank' },
        { text: '  Agent 4: cross-project-regression-detector', style: 'data' },
        { text: '    Catches regressions before they cascade.', style: 'dim' },
        { text: '    Memory: 412 cross-project correlations', style: 'data' },
        { text: '', style: 'blank' },
        { text: '  Shared memory pool: 2,847 entries', style: 'success' },
        { text: '  Agent-to-agent handoffs: 34/day', style: 'data' },
        { text: '', style: 'blank' },
        { text: 'Today: 12 specialized agents share one', style: 'narration' },
        { text: 'memory layer. What one agent learns,', style: 'narration' },
        { text: 'all agents know.', style: 'narration' },
        { text: '', style: 'blank' },
        { text: 'Tomorrow: agents that autonomously', style: 'accent' },
        { text: 'coordinate, negotiate resources, and', style: 'accent' },
        { text: 'resolve conflicts without human routing.', style: 'accent' },
      ],
      hubEvent: {
        task: 'Multi-agent orchestration',
        complexity: 'complex',
        selectedProvider: 'Anthropic',
        selectedModel: 'Opus 4.6',
        cost: 0.48,
        baselineCost: 0.48,
        reason: 'Agent coordination requires frontier reasoning. Opus for the conductor.',
      },
      choices: [
        { label: 'See the continuous learning pipeline', next: 'future-2', tag: 'MLOPS' },
        { label: 'Skip to team memory', next: 'future-3', tag: 'TEAM' },
      ],
    },

    // ── Scene 2: Continuous Learning Pipeline ────────
    {
      id: 'future-2',
      title: 'Continuous Learning Pipeline',
      lines: [
        { text: '$ cortex learning --pipeline', style: 'accent' },
        { text: '', style: 'blank' },
        { text: '=== MLOps for Agent Memory ===', style: 'header' },
        { text: '', style: 'blank' },
        { text: '  Stage 1: CAPTURE', style: 'header' },
        { text: '  Every interaction -> structured event', style: 'dim' },
        { text: '  59,917 events captured (18 months)', style: 'data' },
        { text: '', style: 'blank' },
        { text: '  Stage 2: EXTRACT', style: 'header' },
        { text: '  Events -> insights, patterns, anti-patterns', style: 'dim' },
        { text: '  853 outcomes tracked', style: 'data' },
        { text: '  369 insights derived', style: 'data' },
        { text: '', style: 'blank' },
        { text: '  Stage 3: VALIDATE', style: 'header' },
        { text: '  Confidence calibration against outcomes', style: 'dim' },
        { text: '  Accuracy: 73% (30-day), trending +8%', style: 'success' },
        { text: '', style: 'blank' },
        { text: '  Stage 4: SERVE', style: 'header' },
        { text: '  Sub-second retrieval at point of need', style: 'dim' },
        { text: '  p50 retrieval: 180ms', style: 'data' },
        { text: '', style: 'blank' },
        { text: '  Stage 5: FEEDBACK', style: 'header' },
        { text: '  Outcome tracking closes the loop', style: 'dim' },
        { text: '  47 active feedback loops', style: 'data' },
        { text: '', style: 'blank' },
        { text: 'CAPTURE -> EXTRACT -> VALIDATE -> SERVE -> FEEDBACK', style: 'accent' },
        { text: '   ^                                          |', style: 'accent' },
        { text: '   +------------------------------------------+', style: 'accent' },
        { text: '', style: 'blank' },
        { text: 'This is not a chatbot with a long prompt.', style: 'narration' },
        { text: 'This is an MLOps pipeline for agent intelligence.', style: 'narration' },
      ],
      choices: [
        { label: 'See team memory vision', next: 'future-3', tag: 'TEAM' },
        { label: 'Jump to self-healing codebase', next: 'future-4', tag: 'VISION' },
      ],
    },

    // ── Scene 3: Team Memory ─────────────────────────
    {
      id: 'future-3',
      title: 'Team Memory',
      lines: [
        { text: '$ cortex vision --team-memory', style: 'accent' },
        { text: '', style: 'blank' },
        { text: '=== Team Intelligence Layer (Vision) ===', style: 'header' },
        { text: '', style: 'blank' },
        { text: '  Today: 1 engineer, 6 projects, 1 Cortex instance', style: 'data' },
        { text: '', style: 'blank' },
        { text: '  Tomorrow: 5 engineers, shared memory:', style: 'header' },
        { text: '', style: 'blank' },
        { text: '  Engineer A works on auth refactor.', style: 'narration' },
        { text: '  Discovers: "session tokens must be rotated', style: 'narration' },
        { text: '  on privilege escalation, not just expiry."', style: 'narration' },
        { text: '  Cortex captures this as a security pattern.', style: 'data' },
        { text: '', style: 'blank' },
        { text: '  3 days later:', style: 'dim' },
        { text: '  Engineer C starts a new auth service.', style: 'narration' },
        { text: '  $ cortex intelligence "session token handling"', style: 'accent' },
        { text: '', style: 'blank' },
        { text: '  > Pattern match: "Rotate on privilege escalation"', style: 'success' },
        { text: '  > Source: Engineer A, auth refactor, 3 days ago', style: 'dim' },
        { text: '  > Confidence: 0.91 (production-validated)', style: 'data' },
        { text: '', style: 'blank' },
        { text: '  Engineer C gets Engineer A\'s hard-won', style: 'narration' },
        { text: '  knowledge without asking. Without Slack.', style: 'narration' },
        { text: '  Without a meeting. Without a wiki.', style: 'narration' },
        { text: '', style: 'blank' },
        { text: 'Team knowledge that compounds.', style: 'accent' },
        { text: 'Onboarding that takes days, not months.', style: 'accent' },
        { text: 'Institutional memory that never leaves.', style: 'accent' },
      ],
      choices: [
        { label: 'See the autonomous codebase vision', next: 'future-4', tag: 'VISION' },
        { label: 'Explore another path', next: 'hub', tag: 'PATHS' },
      ],
    },

    // ── Scene 4: The Autonomous Codebase ─────────────
    {
      id: 'future-4',
      title: 'The Autonomous Codebase',
      lines: [
        { text: '$ cortex vision --endgame', style: 'accent' },
        { text: '', style: 'blank' },
        { text: '=== The Endgame: Self-Healing Codebase ===', style: 'header' },
        { text: '', style: 'blank' },
        { text: '  Level 1 (Today): Memory + Routing', style: 'success' },
        { text: '  Session memory persists. Tasks route to', style: 'dim' },
        { text: '  cheapest capable model. Outcomes tracked.', style: 'dim' },
        { text: '', style: 'blank' },
        { text: '  Level 2 (Next): Autonomous Agents', style: 'data' },
        { text: '  Agents detect + fix issues without prompting.', style: 'dim' },
        { text: '  "Test failed at 3am -> agent opens PR by 6am."', style: 'dim' },
        { text: '', style: 'blank' },
        { text: '  Level 3 (Future): Self-Healing Systems', style: 'data' },
        { text: '  Codebases that detect drift, propose fixes,', style: 'dim' },
        { text: '  validate changes, and deploy -- autonomously.', style: 'dim' },
        { text: '  Human reviews the PR. Not the detection.', style: 'dim' },
        { text: '', style: 'blank' },
        { text: '  Level 4 (Vision): Institutional Intelligence', style: 'accent' },
        { text: '  Organizations where every engineering decision', style: 'dim' },
        { text: '  is informed by every past decision.', style: 'dim' },
        { text: '  "How did we solve this 2 years ago?"', style: 'dim' },
        { text: '  Instant answer. Every time.', style: 'dim' },
        { text: '', style: 'blank' },
        { text: '  Current position: Level 1, building Level 2', style: 'success' },
        { text: '  12 agents operational, 2,847 memory entries', style: 'data' },
        { text: '  Foundation laid. Compounding started.', style: 'data' },
        { text: '', style: 'blank' },
        { text: 'The question is not whether AI agents', style: 'narration' },
        { text: 'will have persistent memory.', style: 'narration' },
        { text: '', style: 'blank' },
        { text: 'The question is who builds the learning', style: 'accent' },
        { text: 'loop that makes that memory trustworthy.', style: 'accent' },
        { text: '', style: 'blank' },
        { text: 'That is what Cortex does.', style: 'accent' },
      ],
      choices: [
        { label: 'Explore all paths', next: 'hub', tag: 'PATHS' },
        { label: 'Start over', next: 'hub', tag: 'RESTART' },
      ],
    },
  ],
};
